import { hasPermission } from "./permissionsUtils";

/**
 * Checks if the user has a TradeSupplier role
 * Checks user object as well as stored auth token claims
 * @param {Object} [userObj] - Optional user object or user data from API/context
 * @returns {boolean}
 */
export const isTradeSupplierRole = (userObj) => {
  try {
    const isTradeSupplierString = (str) => {
      if (!str || typeof str !== "string") return false;
      const lower = str.toLowerCase();
      return  lower.includes("trade/supplier");
    };

    if (userObj) {
      if (isTradeSupplierString(userObj.UserRole)) return true;
      if (isTradeSupplierString(userObj.RoleName)) return true;
      if (isTradeSupplierString(userObj.role)) return true;
      if (isTradeSupplierString(userObj.Role)) return true;
      if (Array.isArray(userObj.roles) && userObj.roles.some(isTradeSupplierString)) return true;
      if (typeof userObj.roles === "string" && isTradeSupplierString(userObj.roles)) return true;
    }

    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (isTradeSupplierString(payload.UserRole)) return true;
        if (isTradeSupplierString(payload.RoleName)) return true;
        if (isTradeSupplierString(payload.role)) return true;
        if (isTradeSupplierString(payload.Role)) return true;
        if (isTradeSupplierString(payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"])) return true;
      } catch (e) {
        // ignore parse error
      }
    }

    return false;
  } catch (error) {
    console.error("Error in isTradeSupplierRole:", error);
    return false;
  }
};

// Navigation hierarchy structure
export const navigationMap = {
  Dashboard: {
    name: "Dashboard",
    permissionCode: "DASDASView",
    children: [
      {
        permissionCode: "DASDASView",
        route: "/dashboards",
        name: "Dashboard",
      },
    ],
  },

  Jobs: {
    name: "Jobs",
    children: [
      {
        permissionCode: "JBJOBView",
        route: "/jobs/schedules",
        name: "Jobs Schedules",
      },
      {
        permissionCode: "MGJBJV",
        route: "/jobs",
        name: "Jobs List",
      }
    ],
  },

  WHS: {
    name: "WHS",
    children: [
      {
        permissionCode: "SISOSISOV",
        route: "/whs/sign-in-out",
        name: "Check In & Check Out",
      },
      {
        permissionCode: "INRINRV",
        route: "/whs/incident-report",
        name: "Incident Report",
      },
      {
        permissionCode: "TBTBV",
        route: "/whs/toolbox-talks",
        name: "Toolbox Talks",
      },
      {
        permissionCode: ["VHCVHCV", "VHCASSVEH"],
        route: "/whs/vehicle-register",
        name: "Vehicle Register",
      },
      {
        permissionCode: "VHCVCV",
        route: "/whs/vehicle-start-checklist",
        name: "Vehicle Start Checklist",
      },
      {
        permissionCode: "CONCONV",
        route: "/whs/contractors-list",
        name: "Contractors List",
      }
    ],
  },

  LeadManagement: {
    name: "Lead Management",
    permissionCode: "LEADLEADView",
    children: [
      {
        permissionCode: "LEADLEADView",
        route: "/leads/opportunities",
        name: "Opportunities",
      },
      {
        permissionCode: "LEADLEADView",
        route: "/leads/activities",
        name: "Activities",
      },
      {
        permissionCode: "LEADLEADView",
        route: "/leads/activity-calendar",
        name: "Activity Calendar",
      },
      {
        permissionCode: "FUPSVFUP",
        route: "/followups/calendar",
        name: "Follow-ups",
      },
    ],
  },

  JobsManagement: {
    name: "Jobs Management",
    permissionCode: "JBJOBView",
    children: [
      {
        permissionCode: "MGJBJV",
        route: "/jobs",
        name: "Jobs List",
      },
      {
        permissionCode: "JBJOBView",
        route: "/jobs/schedules/list",
        name: "Schedule List",
      },
      {
        permissionCode: "JBJOBView",
        route: "/jobs/jobs-calendar",
        name: "Job Calendar",
      },
    ],
  },

  WHS: {
    name: "WHS",
    permissionCode: "WHSWHSView",
    children: [
      {
        permissionCode: "SISOSISOV",
        route: "/whs/sign-in-out",
        name: "Check In & Check Out",
      },
      {
        permissionCode: "INRINRV",
        route: "/whs/incident-report",
        name: "Incident Report",
      },
      {
        permissionCode: "TBTBV",
        route: "/whs/toolbox-talks",
        name: "Toolbox Talks",
      },
      {
        permissionCode: ["VHCVHCV", "VHCASSVEH"],
        route: "/whs/vehicle-register",
        name: "Vehicle Register",
      },
      {
        permissionCode: "VHCVCV",
        route: "/whs/vehicle-start-checklist",
        name: "Vehicle Start Checklist",
      },
      {
        permissionCode: "CONCONV",
        route: "/whs/contractors-list",
        name: "Contractors List",
      },
    ],
  },

  Forms: {
    name: "Forms",
    permissionCode: "FDFDView",
    children: [
      {
        permissionCode: "FBVF",
        route: "/forms/form-builder",
        name: "Form Builder",
      },
      {
        permissionCode: "FBVF",
        route: "/forms/master-fields",
        name: "Master Fields",
      },
    ],
  },

  QueryManagement: {
    name: "Query Management",
    permissionCode: "QMQMView",
    children: [
      {
        permissionCode: "QMQMView",
        route: "/query/manage-applicant-queries",
        name: "Manage Applicant Queries",
      },
    ],
  },

  Applications: {
    name: "Applications",
    permissionCode: "ENQRY",
    children: [
      {
        permissionCode: "ENQRY",
        route: "/lead-user/applications",
        name: "Applications",
      },
    ],
  },

  Reports: {
    name: "Reports",
    permissionCode: "RPTRPTView",
    children: [
      {
        permissionCode: "LSRLSRV",
        route: "/reports/lead-source",
        name: "Lead Source Report",
      },
      {
        permissionCode: "LALAV",
        route: "/reports/lead-allocation",
        name: "Lead Allocation Report",
      },
      {
        permissionCode: "LSTLSTV",
        route: "/reports/lead-status",
        name: "Lead Status Report",
      },
    ],
  },

  Settings: {
    name: "Settings",
    permissionCode: "STNGSTNGView",
    children: [
      {
        permissionCode: "APRNCEVIEW",
        route: "/settings/appearance",
        name: "Appearance",
      },
      {
        permissionCode: "USRUSRV",
        route: "/settings/user-management",
        name: "User Management",
      },
      {
        permissionCode: "DESGDSGV",
        route: "/settings/designation-management",
        name: "Designation Management",
      },
      {
        permissionCode: "TEAMTMV",
        route: "/settings/team-management",
        name: "Team Management",
      },
      {
        permissionCode: "TMPTMPV",
        route: "/settings/template-management",
        name: "Template Management",
      },
      {
        permissionCode: "LEADSTGSTGV",
        route: "/settings/lead-stages",
        name: "Lead Stages & Sub-Stages",
      },
      {
        permissionCode: "ROLEROV",
        route: "/settings/role-permissions",
        name: "Role Permissions",
      },
      {
        permissionCode: "RULERV",
        route: "/settings/rule-management",
        name: "Rule Management",
      },
      {
        permissionCode: "INTGVIEW",
        route: "/settings/integrations",
        name: "Integrations",
      },
      {
        permissionCode: "TASVTAS",
        route: "/settings/tags-sources",
        name: "Tags & Sources",
      },
      {
        permissionCode: "CONTVCONT",
        route: "/settings/contacts-management",
        name: "Contacts",
      },
      {
        permissionCode: "ACTVACT",
        route: "/settings/activity-types",
        name: "Activity Types",
      },
      {
        permissionCode: null,
        route: "/settings/general",
        name: "Profile",
      },
    ],
  },
};

/**
 * Find the first available route based on user permissions
 * Special case: TradeSupplier role navigates to Schedule List (/jobs/schedules/list)
 * Iterates through navigation hierarchy and returns first route user has access to
 * @param {Object} [userObj] - Optional user object
 * @returns {string|null} First available route or null if no permissions
 */
export const getFirstAvailableRoute = (userObj) => {
  try {
    // For TradeSupplier role, directly navigate to Schedule List
    if (isTradeSupplierRole(userObj)) {
      return "/jobs/schedules/list";
    }

    // Iterate through each section in navigation map
    for (const [sectionKey, section] of Object.entries(navigationMap)) {
      if (section.permissionCode && !hasPermission(section.permissionCode)) {
        continue;
      }
      if (section.children && Array.isArray(section.children)) {
        for (const child of section.children) {
          if (!child.permissionCode || hasPermission(child.permissionCode)) {
            return child.route;
          }
        }
      }
    }
    // No permission found
    return null;
  } catch (error) {
    console.error("Error finding available route:", error);
    return null;
  }
};

/**
 * Get all accessible routes for the user
 * @returns {Array} Array of accessible routes with metadata
 */
export const getAccessibleRoutes = () => {
  try {
    const accessibleRoutes = [];

    for (const [sectionKey, section] of Object.entries(navigationMap)) {
      if (section.permissionCode && !hasPermission(section.permissionCode)) {
        continue;
      }
      if (section.children && Array.isArray(section.children)) {
        for (const child of section.children) {
          if (!child.permissionCode || hasPermission(child.permissionCode)) {
            accessibleRoutes.push({
              section: section.name,
              ...child,
            });
          }
        }
      }
    }

    return accessibleRoutes;
  } catch (error) {
    console.error("Error getting accessible routes:", error);
    return [];
  }
};

/**
 * Get all sections with their accessible children
 * @returns {Array} Array of sections with accessible children
 */
export const getAccessibleSections = () => {
  try {
    const accessibleSections = [];

    for (const [sectionKey, section] of Object.entries(navigationMap)) {
      if (section.permissionCode && !hasPermission(section.permissionCode)) {
        continue;
      }
      if (section.children && Array.isArray(section.children)) {
        const accessibleChildren = section.children.filter(
          (child) => !child.permissionCode || hasPermission(child.permissionCode)
        );

        if (accessibleChildren.length > 0) {
          accessibleSections.push({
            name: section.name,
            children: accessibleChildren,
          });
        }
      }
    }

    return accessibleSections;
  } catch (error) {
    console.error("Error getting accessible sections:", error);
    return [];
  }
};

